import FixedInput from "@/components/FixedInput";
import Skeleton from "@/components/Skeleton/Skeleton";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import useTranslation from "@/hooks/useTranslation";
import { CheckOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Flex, Input, message } from "antd";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Consumable } from "../ConsumableOverview/ConsumableOverview";
import DeleteProductAction from "../DeleteConsumableAction";
import { useGetConsumable, usePatchConsumable } from "../useConsumables";

export default function ConsumableDetails({ consumableId }: { consumableId: number }) {
  const { data: consumable } = useGetConsumable({ consumableId });
  const patch = usePatchConsumable();
  const smartQueryClient = useSmartQueryClient();
  const t = useTranslation();
  const navigate = useNavigate();

  const saveName = useCallback((name: string) => {
    patch
      .mutateAsync({ consumableId, patch: { name } })
      .catch(() => message.error(t.errors.general));
  }, []);

  const debouncedSaveName = useMemo(() => _.debounce(saveName, 500), [saveName]);

  const updateName = (name: string) => {
    smartQueryClient.setQueryData<Consumable>(
      ["consumables", "detail", consumableId, "details"],
      (consumable) => {
        return consumable
          ? {
              ...consumable,
              name,
            }
          : undefined;
      }
    );

    debouncedSaveName(name);
  };

  return (
    <Flex
      vertical
      gap={24}
    >
      <Flex
        vertical
        gap={8}
      >
        ID
        <Input
          disabled
          value={consumable?.id}
        />
      </Flex>
      <Flex
        vertical
        gap={8}
      >
        Name
        <FixedInput
          value={consumable?.name}
          onChange={(name) => updateName(name)}
        />
      </Flex>
      <Flex
        justify="space-between"
        align="center"
      >
        {consumable ? (
          <DeleteProductAction
            consumable={consumable}
            onSucces={() => navigate("/consumables")}
          >
            <Button
              danger
              disabled={!consumable.deletable}
            >
              <DeleteOutlined /> Delete
            </Button>
          </DeleteProductAction>
        ) : (
          <Skeleton style={{ width: "24px", height: "24px" }} />
        )}
        {patch.isLoading ? (
          <span>
            <LoadingOutlined /> Saving...
          </span>
        ) : (
          <span>
            <CheckOutlined /> Saved
          </span>
        )}
      </Flex>
    </Flex>
  );
}
